import { Component, type ErrorInfo, type ReactNode } from 'react';
import { sporFeil } from '../lib/telemetri';

type Props = {
  children: ReactNode;
  /**
   * Endrer denne seg, glemmer grensa feilen den fanget.
   *
   * Uten den blir en grense stående fanget for alltid: har den først tatt en
   * feil, viser den beskjeden sin til noen laster hele sida på nytt – også
   * etter at brukeren har navigert til noe helt annet som virker utmerket.
   * Rundt en rute settes den til stien, så det å gå videre er nok.
   */
  nullstillNokkel?: string;
  /**
   * `helside` fyller vinduet og brukes ytterst, der det ikke finnes noe skall
   * igjen å vise. `innhold` er panelet inne i skallet, der menyen står og
   * brukeren allerede har en vei videre.
   */
  variant?: 'helside' | 'innhold';
};
type State = { feil: Error | null };

/**
 * Fanger opp en tegnefeil før den blir en hvit skjerm.
 *
 * Uten denne river React ut hele treet når en komponent kaster, og brukeren
 * sitter igjen med en tom side og ingen beskjed. Det er den verste utgaven av
 * en feil: brudeparet vet ikke om det var dem, nettleseren eller oss, og vi får
 * aldri høre om det fordi det ikke finnes noe å klage på.
 *
 * Her får de to ting i stedet – en forklaring på norsk, og en vei videre – og
 * vi får det tredje: en melding i Application Insights med komponentstien, så
 * det går an å finne igjen hvor det skjedde.
 *
 * Klassekomponent fordi `componentDidCatch` og `getDerivedStateFromError` bare
 * finnes der; React har ingen hook som fanger feil.
 */
export class Feilgrense extends Component<Props, State> {
  state: State = { feil: null };

  static getDerivedStateFromError(feil: Error): State {
    return { feil };
  }

  componentDidUpdate(forrige: Props): void {
    if (
      this.state.feil !== null &&
      forrige.nullstillNokkel !== this.props.nullstillNokkel
    ) {
      this.setState({ feil: null });
    }
  }

  componentDidCatch(feil: Error, info: ErrorInfo): void {
    sporFeil(feil, {
      // Komponentstien er det som gjør rapporten brukbar: uten den er en
      // minifisert stakksporing bare bokstaver.
      komponentsti: (info.componentStack ?? '').slice(0, 2000),
    });
    console.error('[feilgrense] en komponent kastet', feil);
  }

  render(): ReactNode {
    if (!this.state.feil) return this.props.children;

    if (this.props.variant === 'innhold') {
      /*
       * Inne i skallet. Menyen står fortsatt, så den viktigste veien videre er
       * allerede der – beskjeden peker på den i stedet for å sende brukeren ut.
       * «Prøv på nytt» nullstiller uten å laste sida: mange tegnefeil kommer av
       * en tilstand som er borte ved neste forsøk. Er den ikke det, står menyen
       * der fortsatt.
       */
      return (
        <div className="feilpanel" role="alert">
          <h2>Dette verktøyet klarte vi ikke å vise</h2>
          <p>
            Feilen er meldt fra om automatisk. Resten av planleggingen virker som før –
            velg et annet verktøy i menyen, eller prøv dette på nytt.
          </p>
          <p className="feilpanel-trygt">
            Det du har lagret er trygt. Det ligger hos oss, ikke i nettleseren.
          </p>
          <div className="feilpanel-knapper">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => this.setState({ feil: null })}
            >
              Prøv på nytt
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => window.location.reload()}
            >
              Last siden på nytt
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="onboarding">
        <div className="onboarding-card center" role="alert">
          <h1>Her gikk noe galt</h1>
          <p className="lead">
            Vi klarte ikke å vise denne siden. Feilen er meldt fra om automatisk, så vi
            vet at den skjedde.
          </p>
          <p>Det du har lagret er trygt – det ligger hos oss, ikke i nettleseren.</p>
          <div className="onboarding-actions">
            {/*
              Vanlig lenke og full lasting, ikke ruteren: er det ruteren eller
              noe under den som kastet, er den ikke til å stole på nå.
            */}
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => window.location.reload()}
            >
              Prøv igjen
            </button>
            <a className="btn btn-secondary" href="/">
              Til forsiden
            </a>
          </div>
        </div>
      </div>
    );
  }
}
